import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faSave,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import * as form from "../../../../form";
import { useFormAction } from "../../../../form";
import ButtonLoader from "../../../ButtonLoader";
import { createEditedAppointmentNote } from "../../../../services/api";
import { ProductSelector } from "../../../ProductSelector";
import { useCurrentLocation } from "../../../../context/LocationContext";
import { ServiceSelector } from "../../../ServiceSelector";
import * as f from "../../../../utils/formatter";

function czPlural(wordSet, n) {
  if (n === 1) return wordSet[0];
  if (n >= 2 && n <= 4) return wordSet[1];
  return wordSet[2];
}

function formatReservation(count, unit) {
  // Explicitly handle null/undefined for both fields
  if (unit === "earliestAvailableDate") return "Nejbližší volný termín";
  if (count == null && (unit == null || unit === ""))
    return "Nepotřebuje termín";
  if (count == null || count === 0) return "Nepotřebuje termín";

  if (unit === "days")
    return `${count} ${czPlural(["den", "dny", "dnů"], count)}`;
  if (unit === "weeks")
    return `${count} ${czPlural(["týden", "týdny", "týdnů"], count)}`;
  if (unit === "months")
    return `${count} ${czPlural(["měsíc", "měsíce", "měsíců"], count)}`;
  return `${count ?? ""} ${unit ?? ""}`.trim();
}

function View({ entry, display }) {
  const service = display?.service;

  return (
    <>
      <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
        <strong>Poznámka:</strong> {display.note_content?.trim() || "---"}
      </Typography>

      {entry?.note_type === "receptionist" && (
        <>
          {service != null && (
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Služba:</strong> {f.value(service?.name)}
            </Typography>
          )}

          {display.service_id != null && (
            <>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Rezervace:</strong>{" "}
                {formatReservation(
                  display.reservation_count,
                  display.reservation_unit,
                )}
              </Typography>

              {typeof display.doctor_required === "boolean" && (
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  <strong>Vyžaduje lékaře:</strong>{" "}
                  {display.doctor_required ? "Ano" : "Ne"}
                </Typography>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

function Form({
  clientId,
  entry,
  viewingSpecificVersion,
  selectedNoteVersion,
  onSave,
  setIsEditing,
}) {
  const isReceptionNote = entry?.note_type === "receptionist";
  const location = useCurrentLocation();
  const [productId, setProductId] = React.useState();

  const initialFormData = useMemo(() => {
    const src = viewingSpecificVersion
      ? (selectedNoteVersion ?? {})
      : (entry ?? {});
    const initial = {
      key: crypto.randomUUID(),
      note_content: src.note_content || "",
    };
    if (isReceptionNote) {
      initial.doctor_required = src.doctor_required ?? false;
      initial.service_id = src.service?.id || "";
      initial.reservation_count =
        typeof src.reservation_count === "number" ? src.reservation_count : 0;
      initial.reservation_unit = src.reservation_unit || "days";
    }

    setProductId(src.service?.product_id);

    return initial;
  }, [viewingSpecificVersion, selectedNoteVersion, entry, isReceptionNote]);

  const handleCancel = () => {
    setIsEditing(false);
  };

  const { onSubmit, loading, errors } = useFormAction(
    async (e) => {
      const formData = {
        previous_id: selectedNoteVersion?.version_id ?? entry?.id ?? null,
        ...form.formData(e.target),
      };

      return createEditedAppointmentNote({
        previous_id: selectedNoteVersion?.version_id || entry.id,
        input: {
          note_content: formData.note_content?.trim() || "",
          doctor_required:
            typeof formData.doctor_required === "boolean"
              ? formData.doctor_required
              : null,
          service_id: formData.service_id || null,
          reservation_count:
            formData.reservation_count !== undefined &&
            formData.reservation_count !== null &&
            formData.reservation_count !== ""
              ? Number(formData.reservation_count)
              : null,
          reservation_unit: formData.reservation_unit || null,
        },
      });
    },
    () => {
      setIsEditing(false);
      onSave();
    },
  );

  return (
    <form key={initialFormData.key} onSubmit={onSubmit}>
      <TextField
        label="Poznámka"
        name="note_content"
        defaultValue={initialFormData.note_content}
        multiline
        rows={isReceptionNote ? 3 : 6}
        fullWidth
        sx={{ mb: isReceptionNote ? 2 : 0 }}
        required={entry?.note_type !== "receptionist"}
      />

      {isReceptionNote && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                name="doctor_required"
                defaultChecked={initialFormData.doctor_required}
              />
            }
            label="Vyžaduje lékaře"
          />

          <Box sx={{ minWidth: 200 }}>
            <ProductSelector
              company_id={location.company_id}
              client_id={clientId}
              onChange={(v) => setProductId(v?.id)}
              default_product_id={productId}
            />
            <ServiceSelector
              key={productId ?? ""}
              location_id={location.id}
              product_id={productId}
              name="service_id"
              default_service_id={initialFormData.service_id}
              truthyFilters={["create_reservation"]}
            />
          </Box>

          <TextField
            size="small"
            type="number"
            label="Počet"
            name="reservation_count"
            defaultValue={initialFormData.reservation_count}
            sx={{ width: 110 }}
            inputProps={{ min: 0 }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Jednotka</InputLabel>
            <Select
              label="Jednotka"
              defaultValue={initialFormData.reservation_unit}
              name="reservation_unit"
            >
              <MenuItem value="days">Dny</MenuItem>
              <MenuItem value="weeks">Týdny</MenuItem>
              <MenuItem value="months">Měsíce</MenuItem>
              <MenuItem value="earliestAvailableDate">
                Nejbližší volný termín
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      <form.FormErrorList formErrors={errors} />

      <Box mt={2} display="flex" gap={1} justifyContent="flex-end">
        <Button
          type="button"
          variant="outlined"
          onClick={handleCancel}
          startIcon={<FontAwesomeIcon icon={faTimes} />}
        >
          Zrušit
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<FontAwesomeIcon icon={faSave} />}
          disabled={loading}
        >
          {loading && <ButtonLoader />}
          Uložit
        </Button>
      </Box>
    </form>
  );
}

export default function InlineNoteEditor({
  clientId,
  entry,
  selectedNoteVersion,
  disabled = false,
  showEditButton = false,
  canEdit = false,
  onSave,
}) {
  const [isEditing, setIsEditing] = useState(false);

  const viewingSpecificVersion = !!selectedNoteVersion;

  const display = useMemo(() => {
    if (viewingSpecificVersion) {
      const v = selectedNoteVersion;
      return {
        note_content: v?.note_content ?? "",
        doctor_required:
          typeof v?.doctor_required === "boolean"
            ? v.doctor_required
            : undefined,
        service_id: v?.service_id ?? null,
        service: v?.service ?? null,
        reservation_count:
          typeof v?.reservation_count === "number" ? v.reservation_count : null,
        reservation_unit: v?.reservation_unit ?? null,
      };
    }
    return {
      note_content: entry?.note_content ?? entry?.note ?? "",
      doctor_required:
        typeof entry?.doctor_required === "boolean"
          ? entry.doctor_required
          : undefined,
      service_id: entry?.service?.id ?? null,
      service: entry?.service ?? null,
      reservation_count:
        typeof entry?.reservation_count === "number"
          ? entry.reservation_count
          : null,
      reservation_unit: entry?.reservation_unit ?? null,
    };
  }, [viewingSpecificVersion, selectedNoteVersion, entry]);

  const toggleEdit = () => setIsEditing(true);

  return (
    <Box sx={{ position: "relative" }}>
      {!disabled && !isEditing && showEditButton && canEdit && (
        <Box sx={{ position: "absolute", top: 0, right: "-1rem", zIndex: 2 }}>
          <Tooltip title="Upravit">
            <IconButton onClick={toggleEdit} sx={{ color: "primary.main" }}>
              <FontAwesomeIcon icon={faPenToSquare} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {isEditing ? (
        <Form
          clientId={clientId}
          entry={entry}
          onSave={onSave}
          selectedNoteVersion={selectedNoteVersion}
          setIsEditing={setIsEditing}
          viewingSpecificVersion={viewingSpecificVersion}
        />
      ) : (
        <View entry={entry} display={display} />
      )}
    </Box>
  );
}
